import auth0, { Auth0Error, Auth0Result } from 'auth0-js';
import {
  AUTH0_API_AUDIENCE,
  AUTH0_DOMAIN_HOST,
  AUTH0_LOGIN_SCOPE,
  SOCIAL_PROVIDER
} from 'constants/auth.constant';
import {
  EMPTY_STRING,
  HTTP_STATS_CODE,
  LOCAL_STORAGE,
  LOCAL_STORAGE_TRUE,
  TOKEN_VALIDATION_CACHE_TTL
} from 'constants/general';
import { PARAMS } from 'constants/params';
import {
  clearLocalStorage,
  getLocalStorage,
  updateLocalStorage
} from 'utils/helpers/storage';
import jwtDecode from 'jwt-decode';
import axios, { AxiosError } from 'axios';
import { BASE_URL } from 'constants/routes';
import Endpoints from 'constants/endpoints';
import { t } from './i18n.service';
import { Auth0User, AuthCallBack } from 'interfaces/auth.interface';
import { getQueryParam } from 'utils/routes.util';
import { AUTH_EVENT } from 'constants/events';
import { verifyApiAccessToken } from 'utils/api-access-token-verify.util';
import { isAccessTokenValid } from 'utils/auth-token-validation.util';
import { exchangeRefreshTokenGrant } from 'utils/auth-refresh.util';
import { captureException } from './posthog.service';
import { disconnectPusherClient } from './pusher-client';
import { clearNoteImageUrlCache } from './note-images/note-image-url-cache';

type SocialProvider = (typeof SOCIAL_PROVIDER)[keyof typeof SOCIAL_PROVIDER];

const auth = new auth0.WebAuth({
  domain: AUTH0_DOMAIN_HOST,
  clientID: process.env.REACT_APP_AUTH0_CLIENT_ID ?? '',
  audience: AUTH0_API_AUDIENCE
});

let isGoogleLoginInFlight = false;
let isAppleLoginInFlight = false;

export const login = async (
  username: string,
  password: string,
  callback: AuthCallBack
) => {
  try {
    auth.client.login(
      {
        realm: 'Username-Password-Authentication',
        username,
        password,
        audience: AUTH0_API_AUDIENCE,
        scope: AUTH0_LOGIN_SCOPE
      },
      (error, authResult) => loginCallback(error, authResult, callback)
    );
  } catch {
    callback(true, t('could_not_process_the_request'));
  }
};

export const register = async (
  email: string,
  password: string,
  callback: AuthCallBack
) => {
  try {
    auth.signup(
      {
        connection: 'Username-Password-Authentication',
        email,
        password,
        username: getUsernameFromEmail(email)
      },
      function (err) {
        if (err) {
          callback(true, err.description || t('could_not_process_the_request'));
          return;
        }
        callback(false);
      }
    );
  } catch {
    callback(true, t('could_not_process_the_request'));
  }
};

export const logout = async () => {
  try {
    disconnectPusherClient();
    clearNoteImageUrlCache();
    const savedTheme = getLocalStorage(LOCAL_STORAGE.THEME);
    auth.logout({
      clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
      returnTo: process.env.REACT_APP_AUTH0_LOGOUT_REDIRECT_URI
    });
    clearLocalStorage();
    if (savedTheme) {
      updateLocalStorage(LOCAL_STORAGE.THEME, savedTheme);
    }
  } catch {
    t('could_not_process_the_request');
  }
};

export const checkAuthStatus = async () => {
  try {
    const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
    if (!token) {
      return await checkUserSession();
    }

    return isAccessTokenValid(token);
  } catch {
    return false;
  }
};

export const getAuth0User = async (): Promise<Auth0User | null> => {
  try {
    const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
    const idToken = getLocalStorage(LOCAL_STORAGE.AUTH_ID);
    let decodedToken: Auth0User | null = null;

    if (idToken) {
      try {
        decodedToken = jwtDecode(idToken);
      } catch {
        decodedToken = null;
      }
    }

    if (!decodedToken && token) {
      const user = await new Promise((resolve, reject) => {
        auth.client.userInfo(token, (err, user) => {
          if (err || !user) {
            return reject(null);
          }
          resolve(user);
        });
      });
      decodedToken = user as Auth0User;
    }

    return decodedToken;
  } catch {
    return null;
  }
};

export const resetPassword = async (
  email: string,
  callback: (
    hasError: boolean,
    error?: string,
    isEmailNotVerified?: boolean
  ) => void
) => {
  axios({
    baseURL: BASE_URL,
    url: Endpoints.AUTH_RESET_PASSWORD,
    method: 'POST',
    data: { email }
  })
    .then((response) => {
      const { status, data } = response;
      if (status === HTTP_STATS_CODE.CREATED) {
        callback(false);
      } else {
        callback(true, data);
      }
    })
    .catch((error: AxiosError) => {
      const message = error?.response?.data;
      callback(
        true,
        typeof message === 'string'
          ? message
          : t('could_not_process_the_request'),
        error.status === HTTP_STATS_CODE.FORBIDDEN
      );
    });
};

export const sendEmailVerification = async (
  email: string,
  callback: AuthCallBack
) => {
  axios({
    baseURL: BASE_URL,
    url: Endpoints.AUTH_SEND_EMAIL_VERIFICATION,
    method: 'POST',
    data: { email }
  })
    .then((response) => {
      if (response.status === HTTP_STATS_CODE.CREATED) {
        callback(false);
      } else {
        callback(true, response.data);
      }
    })
    .catch(() => {
      callback(true, EMPTY_STRING);
    });
};

export const loginWithGoogle = (callback: AuthCallBack) => {
  if (isGoogleLoginInFlight) return;

  isGoogleLoginInFlight = true;

  try {
    auth.popup.authorize(
      {
        domain: AUTH0_DOMAIN_HOST,
        connection: 'google-oauth2',
        audience: AUTH0_API_AUDIENCE ?? '',
        scope: AUTH0_LOGIN_SCOPE,
        redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI ?? '',
        responseMode: 'form_post',
        responseType: 'token id_token',
        owp: true
      },
      (error, authResult) =>
        loginCallback(error, authResult, callback, SOCIAL_PROVIDER.GOOGLE)
    );
  } catch {
    isGoogleLoginInFlight = false;
    callback(true, t('could_not_process_the_request'));
  }
};

export const loginWithApple = (callback: AuthCallBack) => {
  if (isAppleLoginInFlight) return;

  isAppleLoginInFlight = true;

  try {
    auth.popup.authorize(
      {
        domain: AUTH0_DOMAIN_HOST,
        connection: 'apple',
        audience: AUTH0_API_AUDIENCE ?? '',
        scope: AUTH0_LOGIN_SCOPE,
        redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI ?? '',
        responseMode: 'form_post',
        responseType: 'token id_token',
        owp: true
      },
      (error, authResult) =>
        loginCallback(error, authResult, callback, SOCIAL_PROVIDER.APPLE)
    );
  } catch {
    isAppleLoginInFlight = false;
    callback(true, t('could_not_process_the_request'));
  }
};

const getUsernameFromEmail = (email: string) => email.split('@')[0];

const checkUserSession = () => {
  return new Promise((resolve: (arg: boolean) => void, reject) => {
    auth.checkSession(
      {
        responseMode: 'form_post',
        responseType: 'token id_token',
        redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI,
        audience: AUTH0_API_AUDIENCE,
        scope: AUTH0_LOGIN_SCOPE
      },
      (error, authResult) => {
        if (error) {
          reject(error);
        } else {
          if (authResult?.access_token) {
            updateLocalStorage(
              LOCAL_STORAGE.AUTH_BEARER_TOKEN,
              authResult.access_token
            );
            resolve(true);
          } else {
            resolve(false);
          }
        }
      }
    );
  });
};

export const isDesktopLoginRequest =
  getQueryParam(PARAMS.SIGN_INTO_DESKTOP) === LOCAL_STORAGE_TRUE ||
  sessionStorage.getItem(LOCAL_STORAGE.DESKTOP_SIGNIN_IN_PROGRESS) ===
    LOCAL_STORAGE_TRUE;

const releaseSocialLoginLock = (provider?: SocialProvider) => {
  if (provider === SOCIAL_PROVIDER.GOOGLE) {
    isGoogleLoginInFlight = false;
  }

  if (provider === SOCIAL_PROVIDER.APPLE) {
    isAppleLoginInFlight = false;
  }
};

const loginCallback = (
  error: Auth0Error | null,
  authResult: Auth0Result,
  callback: AuthCallBack,
  provider?: SocialProvider
) => {
  releaseSocialLoginLock(provider);

  if (error) {
    callback(
      true,
      error.original === 'User closed the popup window'
        ? EMPTY_STRING
        : error.description || t('could_not_process_the_request')
    );
    return;
  }

  if (authResult?.accessToken) {
    updateLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN, authResult.accessToken);

    if (authResult.idToken) {
      updateLocalStorage(LOCAL_STORAGE.AUTH_ID, authResult.idToken);
    }

    if (authResult.refreshToken) {
      updateLocalStorage(
        LOCAL_STORAGE.AUTH_BEARER_REFRESH,
        authResult.refreshToken
      );
    }

    if (isDesktopLoginRequest) {
      sessionStorage.setItem(
        LOCAL_STORAGE.AUTH_BEARER_TOKEN_DESKTOP,
        authResult.accessToken
      );
      sessionStorage.setItem(LOCAL_STORAGE.DESKTOP_SIGNIN_IN_PROGRESS, 'true');
    }

    callback(false, undefined);
    window.dispatchEvent(new Event(AUTH_EVENT.UPDATED, { composed: true }));
    return;
  }

  callback(true, t('could_not_process_the_request'));
};
export const verifyEmailAddress = async (
  token: string,
  callback: AuthCallBack
) => {
  axios({
    baseURL: BASE_URL,
    url: Endpoints.AUTH_VERIFY_EMAIL,
    method: 'GET',
    params: {
      token
    }
  })
    .then((response) => {
      if (response.status === HTTP_STATS_CODE.SUCCESS) {
        callback(false);
      } else {
        callback(true, response.data);
      }
    })
    .catch((error: AxiosError) => {
      const message = error.response?.data;
      callback(
        true,
        typeof message === 'string'
          ? message
          : t('could_not_process_the_request')
      );
    });
};

const tokenValidationCache = new Map<
  string,
  { isValid: boolean; timestamp: number }
>();

export const validateAuth0Token = async (token: string): Promise<boolean> => {
  if (!token) {
    return false;
  }

  const cached = tokenValidationCache.get(token);
  const now = Date.now();

  if (cached && now - cached.timestamp < TOKEN_VALIDATION_CACHE_TTL) {
    return cached.isValid;
  }

  try {
    const isValid = await verifyApiAccessToken(token);

    tokenValidationCache.set(token, { isValid, timestamp: now });

    if (tokenValidationCache.size > 100) {
      const entriesToDelete: string[] = [];
      tokenValidationCache.forEach((value, key) => {
        if (now - value.timestamp >= TOKEN_VALIDATION_CACHE_TTL) {
          entriesToDelete.push(key);
        }
      });
      entriesToDelete.forEach((key) => tokenValidationCache.delete(key));
    }

    return isValid;
  } catch (error) {
    captureException('Token validation failed: JWT verification error', {
      error
    });

    tokenValidationCache.set(token, {
      isValid: false,
      timestamp: now
    });

    return false;
  }
};

export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    return await exchangeRefreshTokenGrant(true);
  } catch (error) {
    captureException('Token refresh error', { error });
    return false;
  }
};
